import { Terbilang } from "../../utils/Terbilang";

/**
 * Convert BigInt to Indonesian words using existing Terbilang class
 * @param amount BigInt amount (in Rupiah)
 * @returns Indonesian text representation
 */
export function terbilangBigInt(amount: bigint): string {
	return Terbilang.ConvertBigInt(amount);
}

/**
 * Convert currency amount to Indonesian words with "rupiah"
 * @param amount BigInt amount in Rupiah
 * @returns Formatted text like "lima juta rupiah"
 */
export function terbilangRupiah(amount: bigint): string {
	if (amount === 0n) {
		return "nol rupiah";
	}

	const baseText = terbilangBigInt(amount);
	return `${baseText} rupiah`;
}

/**
 * Convert inheritance fraction to readable text
 * @param num Numerator
 * @param den Denominator
 * @returns Text like "satu per dua" for 1/2
 */
export function terbilangFraction(num: bigint, den: bigint): string {
	if (den === 0n) {
		throw new Error("Denominator cannot be zero");
	}

	if (num === 0n) {
		return "nol";
	}

	if (den === 1n) {
		return terbilangBigInt(num);
	}

	const numText = terbilangBigInt(num);
	const denText = terbilangBigInt(den);

	return `${numText} per ${denText}`;
}

/**
 * Convert inheritance share description to Indonesian
 * @param heirType Type of heir (e.g., 'istri', 'anakLaki')
 * @param count Number of heirs in this category
 * @returns Descriptive text like "satu orang istri" or "dua orang anak laki-laki"
 */
export function terbilangHeirDescription(
	heirType: string,
	count: number,
): string {
	const descriptions: Record<string, string> = {
		// Spouses
		suami: "Suami",
		istri: count > 1 ? `${count} Istri` : "Istri",

		// Parents
		ayah: "Ayah",
		ibu: "Ibu",

		// Grandparents
		kakekAyah: "Kakek (dari Ayah)",
		nenekAyah: "Nenek (dari Ayah)",
		nenekIbu: "Nenek (dari Ibu)",
		kakekIbu: "Kakek (dari Ibu)",

		// Children
		anakLaki: count > 1 ? `${count} Anak Laki-laki` : "Anak Laki-laki",
		anakPerempuan: count > 1 ? `${count} Anak Perempuan` : "Anak Perempuan",

		// Grandchildren from sons
		cucuLakiDariAnakLaki: count > 1 ? `${count} Cucu Laki-laki (dari Anak Laki)` : "Cucu Laki-laki (dari Anak Laki)",
		cucuPerempuanDariAnakLaki: count > 1 ? `${count} Cucu Perempuan (dari Anak Laki)` : "Cucu Perempuan (dari Anak Laki)",

		// Siblings
		saudaraLakiKandung: count > 1 ? `${count} Saudara Laki-laki Kandung` : "Saudara Laki-laki Kandung",
		saudaraPerempuanKandung: count > 1 ? `${count} Saudara Perempuan Kandung` : "Saudara Perempuan Kandung",
		saudaraLakiSeayah: count > 1 ? `${count} Saudara Laki-laki Seayah` : "Saudara Laki-laki Seayah",
		saudaraPerempuanSeayah: count > 1 ? `${count} Saudara Perempuan Seayah` : "Saudara Perempuan Seayah",
		saudaraLakiSeibu: count > 1 ? `${count} Saudara Laki-laki Seibu` : "Saudara Laki-laki Seibu",
		saudaraPerempuanSeibu: count > 1 ? `${count} Saudara Perempuan Seibu` : "Saudara Perempuan Seibu",

		// Nephews and uncles
		keponakanLakiDariSaudaraLakiKandung: count > 1 ? `${count} Keponakan Laki-laki (dari Saudara Laki Kandung)` : "Keponakan Laki-laki (dari Saudara Laki Kandung)",
		pamanKandung: count > 1 ? `${count} Paman Kandung` : "Paman Kandung",
		pamanSeayah: count > 1 ? `${count} Paman Seayah` : "Paman Seayah",

		// === DHUWU AL-ARHAM ===
		// Grandchildren from daughters
		cucuLakiDariAnakPerempuan: count > 1 ? `${count} Cucu Laki-laki (dari Anak Perempuan)` : "Cucu Laki-laki (dari Anak Perempuan)",
		cucuPerempuanDariAnakPerempuan: count > 1 ? `${count} Cucu Perempuan (dari Anak Perempuan)` : "Cucu Perempuan (dari Anak Perempuan)",

		// Maternal relatives
		pamanDariIbu: count > 1 ? `${count} Paman (dari Ibu)` : "Paman (dari Ibu)",

		// Aunts
		bibiKandung: count > 1 ? `${count} Bibi Kandung` : "Bibi Kandung",
		bibiSeayah: count > 1 ? `${count} Bibi Seayah` : "Bibi Seayah",
		bibiDariIbu: count > 1 ? `${count} Bibi (dari Ibu)` : "Bibi (dari Ibu)",

		// Female nephews/nieces
		keponakanPerempuanDariSaudaraKandung: count > 1 ? `${count} Keponakan Perempuan (dari Saudara Kandung)` : "Keponakan Perempuan (dari Saudara Kandung)",
		keponakanPerempuanDariSaudaraSeayah: count > 1 ? `${count} Keponakan Perempuan (dari Saudara Seayah)` : "Keponakan Perempuan (dari Saudara Seayah)",

		// Cousins
		sepupuLakiKandung: count > 1 ? `${count} Sepupu Laki-laki Kandung` : "Sepupu Laki-laki Kandung",
		sepupuPerempuanKandung: count > 1 ? `${count} Sepupu Perempuan Kandung` : "Sepupu Perempuan Kandung",
		sepupuLakiSeayah: count > 1 ? `${count} Sepupu Laki-laki Seayah` : "Sepupu Laki-laki Seayah",
		sepupuPerempuanSeayah: count > 1 ? `${count} Sepupu Perempuan Seayah` : "Sepupu Perempuan Seayah",

		// Children of nephews
		anakKeponakanLakiKandung: count > 1 ? `${count} Anak Keponakan Laki-laki Kandung` : "Anak Keponakan Laki-laki Kandung",
		anakKeponakanPerempuanKandung: count > 1 ? `${count} Anak Keponakan Perempuan Kandung` : "Anak Keponakan Perempuan Kandung",
	};

	return descriptions[heirType] || `${heirType} (${count})`;
}
